import React from "react";
import {
  Alert,
  Box,
  Button,
  KeyValuePairs,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsReport } from "../../lusca-search-api/generated-src";
import OMSReportFreshness from "./OMSReportFreshness";
import { reportTimestamp } from "./OMSReportSchedule";
import { fileSize } from "./omsReportFormatting";

export default function OMSExportSource({
  report,
  availableReport,
  accepted,
  onUseLatest,
  compact = false,
}: {
  report: OmsReport;
  availableReport: OmsReport;
  accepted: boolean;
  onUseLatest: () => void;
  compact?: boolean;
}) {
  const newer = report.snapshotId !== availableReport.snapshotId;
  const heading =
    newer && accepted ? "Original source report" : "Source report";
  return (
    <section aria-label="Source report">
      <SpaceBetween size="s">
        <div className="oms-report-download__section-heading">
          <Box variant={compact ? "span" : "h3"}>
            {compact ? (
              <>
                <strong>Data refreshed: </strong>
                <time dateTime={report.sourceRefreshedAt}>
                  {reportTimestamp(report.sourceRefreshedAt)}
                </time>
              </>
            ) : (
              heading
            )}
          </Box>
          <Popover
            dismissAriaLabel="Close report details"
            header="Source snapshot"
            size="large"
            triggerType="text"
            content={
              <SpaceBetween size="s">
                {compact && (
                  <KeyValuePairs
                    columns={2}
                    items={[
                      {
                        label: "Source rows",
                        value: report.rowCount.toLocaleString(),
                      },
                      {
                        label: "Source size",
                        value: fileSize(report.totalBytes),
                      },
                    ]}
                  />
                )}
                <Box>Snapshot ID: {report.snapshotId}</Box>
                <Box>Published: {reportTimestamp(report.exportedAt)}</Box>
              </SpaceBetween>
            }
          >
            {compact ? "Report details" : "Snapshot details"}
          </Popover>
        </div>
        {!compact && (
          <KeyValuePairs
            columns={3}
            items={[
              {
                label: "Data refreshed",
                value: (
                  <time dateTime={report.sourceRefreshedAt}>
                    {reportTimestamp(report.sourceRefreshedAt)}
                  </time>
                ),
              },
              { label: "Source rows", value: report.rowCount.toLocaleString() },
              { label: "Source size", value: fileSize(report.totalBytes) },
            ]}
          />
        )}
        <OMSReportFreshness sourceRefreshedAt={report.sourceRefreshedAt} />
        {newer && (
          <Alert
            type="info"
            header="A newer source report is available"
            action={
              !accepted && (
                <Button onClick={onUseLatest}>Use newer report</Button>
              )
            }
          >
            {accepted
              ? "This export keeps its original source. Choose Edit settings, then Use newer report to apply your settings to newer data."
              : "This configuration still uses the source shown. Choose Use newer report to apply it to the newer source."}{" "}
            Newer data refreshed:{" "}
            {reportTimestamp(availableReport.sourceRefreshedAt)}.
          </Alert>
        )}
      </SpaceBetween>
    </section>
  );
}
